/**
 * ProAlertsDashboard - Equipe Hana / Medecin view
 * ================================================
 * Liste les patients ayant soumis des questionnaires PRO avec grade >= 3
 * ou des scores EORTC preoccupants. Action rapide : appeler / messagerie.
 */
import { useState, useEffect, useCallback } from "react";
import api from "@/api";
import { toast } from "sonner";
import {
  AlertTriangle, Phone, MessageSquare, ClipboardList,
  RefreshCw, User, Clock, ChevronRight, Filter,
} from "lucide-react";

const SEVERITY_COLORS = {
  CRITICAL: "bg-red-50 text-red-700 border-red-200",
  HIGH: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function ProAlertsDashboard() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minGrade, setMinGrade] = useState(3);

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/studies/reports/pro-alerts?min_grade=${minGrade}`);
      setAlerts(res.data?.alerts || []);
    } catch {
      toast.error("Erreur chargement des alertes PRO");
    } finally {
      setLoading(false);
    }
  }, [minGrade]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  return (
    <div className="space-y-4" data-testid="pro-alerts-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Alertes PRO-CTCAE
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Patients ayant déclaré des symptômes sévères (grade ≥ {minGrade}). Triage priorité équipe Hana.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Filter */}
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <select
              value={minGrade}
              onChange={e => setMinGrade(parseInt(e.target.value))}
              data-testid="min-grade-select"
              className="text-xs bg-transparent border-0 focus:outline-none"
            >
              <option value={2}>Grade ≥ 2 (Modéré)</option>
              <option value={3}>Grade ≥ 3 (Sévère)</option>
              <option value={4}>Grade 4 (Très sévère)</option>
            </select>
          </div>
          <button
            onClick={fetchAlerts}
            data-testid="refresh-alerts-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard
          label="Alertes actives"
          value={alerts.length}
          color="text-amber-600"
          bg="bg-amber-50"
          testId="stat-total"
        />
        <StatCard
          label="Critiques (grade 4)"
          value={alerts.filter(a => a.severityLevel === "CRITICAL").length}
          color="text-red-600"
          bg="bg-red-50"
          testId="stat-critical"
        />
        <StatCard
          label="Patients uniques"
          value={new Set(alerts.map(a => a.patientId)).size}
          color="text-indigo-600"
          bg="bg-indigo-50"
          testId="stat-patients"
        />
      </div>

      {/* Alerts list */}
      {loading ? (
        <div className="text-center py-12 text-slate-400" data-testid="alerts-loading">Chargement...</div>
      ) : alerts.length === 0 ? (
        <div className="text-center py-16 bg-emerald-50 border border-emerald-100 rounded-2xl" data-testid="alerts-empty">
          <AlertTriangle className="w-10 h-10 mx-auto text-emerald-400 mb-2" />
          <p className="font-semibold text-emerald-800">Aucune alerte active</p>
          <p className="text-sm text-emerald-600 mt-1">
            Aucun patient n'a déclaré de symptômes sévères récemment.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="alerts-list">
          {alerts.map(alert => (
            <AlertRow key={alert.responseId} alert={alert} />
          ))}
        </div>
      )}
    </div>
  );
}


function AlertRow({ alert }) {
  const navigateToPatient = () => {
    window.location.href = `/doctor/patients/${alert.patientId}`;
  };

  return (
    <div
      className={`border rounded-xl p-4 transition-all hover:shadow-sm ${
        SEVERITY_COLORS[alert.severityLevel] || "bg-slate-50 text-slate-700 border-slate-200"
      }`}
      data-testid={`alert-row-${alert.responseId}`}
    >
      <div className="flex items-center gap-4">
        {/* Severity badge */}
        <div className="flex-shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-lg bg-white/60 border border-current/20">
          <span className="text-[10px] uppercase tracking-wider font-semibold">Grade</span>
          <span className="text-2xl font-bold leading-none">{alert.maxGrade || "?"}</span>
        </div>

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-4 h-4 flex-shrink-0" />
            <p className="font-semibold truncate">{alert.patientName}</p>
            {alert.cancerType && (
              <span className="px-2 py-0.5 bg-white/60 rounded-full text-[10px] font-medium">
                {alert.cancerType}
              </span>
            )}
            {alert.currentCycleNumber && (
              <span className="px-2 py-0.5 bg-white/60 rounded-full text-[10px] font-medium">
                C{alert.currentCycleNumber}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs opacity-80 mb-1">
            <ClipboardList className="w-3.5 h-3.5" />
            <span>{alert.questionnaireName} · {alert.visitName}</span>
            {alert.completedAt && (
              <>
                <span>·</span>
                <Clock className="w-3.5 h-3.5" />
                <span>{new Date(alert.completedAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </>
            )}
          </div>
          <ul className="text-xs space-y-0.5 mt-2">
            {alert.alertReasons.map((r, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="flex-shrink-0 mt-1.5 w-1 h-1 rounded-full bg-current" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 flex flex-col gap-1.5">
          {alert.patientPhone && (
            <a
              href={`tel:${alert.patientPhone}`}
              data-testid={`call-patient-${alert.patientId}`}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 text-current rounded-lg text-xs font-semibold hover:bg-white transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              Appeler
            </a>
          )}
          <button
            onClick={navigateToPatient}
            data-testid={`open-patient-${alert.patientId}`}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 text-current rounded-lg text-xs font-semibold hover:bg-white transition-colors"
          >
            <ChevronRight className="w-3.5 h-3.5" />
            Ouvrir
          </button>
        </div>
      </div>
    </div>
  );
}


function StatCard({ label, value, color, bg, testId }) {
  return (
    <div className={`${bg} rounded-xl p-4`} data-testid={testId}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-2xl font-bold ${color} mt-1`}>{value}</p>
    </div>
  );
}
